import React, {
  useState,
  useEffect,
  useCallback,
  memo,
  useRef,
  useMemo,
} from 'react';
import { Table, TableProps } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons';
import { getColumns } from './utils/common';
import { getNumberOfDotUnit } from '@gp/utils/common';
import classNames from 'classnames';
import { TableListPropsType } from './index.d';
import '@gp/components/MultipleLinesTextBubble/index.scss';
import './index.scss';

const { autotip, groupable, sortable, pageable, scrollable, injectable } =
  Table.addons;

const DEFAULT_PROPS: TableListPropsType = {
  className: '',
  loading: true,
  tableRecords: null, // 数组每一项要有uniqueId
  tableOptions: null,
  onSort: null,
  onPagingChange: null,
  onVirtualLoading: null,
  isTableBodyMaxH: true,
  triggerFoldExpand: false,
  bordered:"all",
};

function TableList(props: TableListPropsType) {
  const {
    className,
    loading: propsLoading,
    tableRecords,
    tableOptions,
    isTableBodyMaxH,
    onSort,
    onPagingChange,
    onVirtualLoading,
    triggerFoldExpand,
    bordered,
  } = props;
  const [records, setRecords] = useState<any[]>([]);
  const [sorts, setSorts] = useState<SortBy<any>>({
    by: tableOptions?.sortableOptions?.defaultSort?.by || '',
    order: tableOptions?.sortableOptions?.defaultSort?.order,
  });
  const [paginationMsg, setPaginationMsg] = useState<{
    pageIndex: number;
    pageSize: number;
    total: number;
  }>({
    pageIndex: 1,
    pageSize: tableOptions?.pageableOptions?.pageSizeOptions?.[0] || 20,
    total: 0,
  });
  const recordsTotalRef = useRef<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [tableBodyMaxH, setTableBodyMaxH] = useState<number>(500);
  // 表格是否出现纵向滚动条
  const [tableIsScroll, setTableIsScroll] = useState<boolean>(false);
  const tableIsScrollRef = useRef<boolean>(false);
  // 展开的group列
  const [unFoldList, setUnFoldList] = useState<string[]>([]);
  const tableRef = useRef<HTMLTableElement>(null);
  const tableBodyRef = useRef<HTMLElement | (any & {})>(null);

  // 虚假的loading，目的是为了让横纵滚动条回到初始位置
  const resetScroll = useCallback(() => {
    onVirtualLoading?.(true);
    setLoading(true);
    const t = setTimeout(() => {
      clearTimeout(t);
      setLoading(false);
      onVirtualLoading?.(false);
    }, 50);
  }, []);

  // 最后一列key名称
  const lastColKeyName = useMemo(() => {
    const columsOptions = tableOptions?.columsOptions;
    let keyName = '';
    if (!Array.isArray(columsOptions)) return keyName;
    for (let i = 0, l = columsOptions?.length; i < l; i++) {
      if (i === l - 1) {
        const t = columsOptions?.[i];
        keyName = t?.keyName;
      }
    }

    return keyName;
  }, [tableOptions?.columsOptions]);

  // 折叠相关子列key名称
  const groupChildKeys = useMemo(() => {
    const columsOptions = tableOptions?.columsOptions;
    let keys: string[] = [];
    if (!Array.isArray(columsOptions)) return keys;
    for (let i = 0, l = columsOptions?.length; i < l; i++) {
      const t = columsOptions?.[i];
      if (t?.isGroupCol && !t?.isGroup && t?.groupKey) {
        keys.push(t?.keyName);
      }
    }

    return keys;
  }, [tableOptions?.columsOptions]);

  const pageSizeOptions = tableOptions?.pageableOptions?.pageSizeOptions || [20, 50, 100];
  const showPageableOperation = paginationMsg?.total > Math.min(...pageSizeOptions);
  // 表格使用到的插件
  const addons = [
    scrollable({
      maxHeight: isTableBodyMaxH ? tableBodyMaxH : 'unset',
    }),
    pageable({
      stateTextVisible: true,
      pageSizeVisible: showPageableOperation,
      pageIndexVisible: showPageableOperation,
      jumpVisible: showPageableOperation,
      endJumpVisible: showPageableOperation,
      pageIndex: paginationMsg?.pageIndex,
      pageSize: paginationMsg?.pageSize,
      pageSizeOptions,
      recordCount: paginationMsg?.total,
      stateText: `${getNumberOfDotUnit(paginationMsg?.total)} item${paginationMsg?.total > 1 ? 's' : ''} in total`,
      onPagingChange: ({ pageIndex, pageSize }) => {
        const newPagination = { ...paginationMsg, pageIndex: pageIndex as number, pageSize: pageSize as number };
        setPaginationMsg(newPagination);
        onPagingChange?.(newPagination);
        resetScroll();
      },
    }),
    sortable({
      columns: tableOptions?.sortableOptions?.sortColumns || [],
      value: sorts ? [sorts] : [],
      onChange: (value) => {
        setSorts(value?.[0]);
        onSort?.(value?.[0]);
        setPaginationMsg({ ...paginationMsg, pageIndex: 1 });
        // resetScroll();
      },
    }),
    injectable({
      cell: (props, context) => {
        const key = context?.column?.key;
        return {
          // className: `${
          //   // 有排序的列，单元格加特殊类名标识
          //   (tableOptions?.sortableOptions?.sortColumns || [])?.includes(key)
          //     ? 'gp-general-table-sort-cell'
          //     : ''
          // } ${
          //   // 最后一列单元格加特殊类名标识
          //   key === lastColKeyName
          //     ? 'gp-general-table-last-col-cell'
          //     : ''
          // } ${
          //   // 折叠相关子列单元格加特殊类名标识
          //   groupChildKeys?.includes(key)
          //     ? 'gp-general-table-group-child-col-cell'
          //     : ''
          // }`,
          className: `${
            // 折叠相关子列单元格加特殊类名标识
            groupChildKeys?.includes(key) ? 'table-expand-col' : ''
          } ${groupChildKeys?.includes(key) && triggerFoldExpand ? 'show' : 'hide'}`,
          // style: {
          //   ...(props.style || {}),
          // },
        };
      },
    }),
    autotip({
      isLoading: loading,
      hideIcon: !loading, // loading状态显示图标，其他情况不显示
      emptyText: 'The list has no data yet.',
      style: {
        height: 172,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
      },
    }),
  ];

  // 表头分组
  if (Array.isArray(tableOptions?.headerGroups)) {
    addons.push(
      groupable({
        headerGroups: tableOptions?.headerGroups || [],
      }),
    );
  }

  // 判断横向是否出现滚动条（由于border样式影响，当不需要横向滚动条的时候也出现了），以及纵向是否出现滚动条、表体最大高度计算
  const handleResize = useCallback(() => {
    const tableBody = document.querySelector(`.${className} .tea-table__body`);
    if (tableBody) {
      tableBodyRef.current = tableBody;
    }
    // @ts-ignore
    const flag1 = tableBody?.offsetWidth < tableBody?.firstChild?.offsetWidth;
    // @ts-ignore
    // tableBody.style.overflowX = flag1 ? 'overlay' : 'hidden';
    // @ts-ignore 纵向是否出现滚动条
    const flag2 = tableBody?.clientHeight < tableBody?.firstChild?.clientHeight;
    setTableIsScroll(flag2);
    tableIsScrollRef.current = flag2;
    // 表体最大高度计算
    if (!tableOptions?.tableBodyOptions?.isAutoadaptationBodyHeight) return;
    const outerContainer = document.querySelector(
      `.${
        tableOptions?.tableBodyOptions?.outerContainerClassName ||
        'tea-layout__content'
      }`,
    );
    // @ts-ignore
    const h =
      outerContainer?.clientHeight > 0
        ? outerContainer?.clientHeight -
          tableOptions?.tableBodyOptions?.subtractHeight -
          (recordsTotalRef.current > 20 ? 50 : 0)
        : 500;
    setTableBodyMaxH(h);
  }, []);

  // 当表格出现纵向滚动条时，加最大可滚动限制
  const handleScroll = useCallback((e) => {
    if (!tableIsScrollRef.current) return;
    // 出现了滚动条时，设置最大可滚动限制
    const currentWidth = e.target.offsetWidth,
      scrollLeft = e.target.scrollLeft,
      scrollWidth = e.target.scrollWidth;
    const maxScrollLeft = scrollWidth - currentWidth - 9; // 滚动条宽度为9
    if (scrollLeft > maxScrollLeft && e) {
      e.target.scrollLeft = maxScrollLeft;
    }
  }, []);

  // 判断横向是否出现滚动条（由于border样式影响，当不需要横向滚动条的时候也出现了）
  // useEffect(() => {
  //   if (loading) return;
  //   handleResize();
  // }, [unFoldList?.length, loading]);

  // 初始化加监听
  // useEffect(() => {
  //   window.addEventListener('resize', handleResize);
  //   // 给表格添加滚动监听
  //   const t = setInterval(() => {
  //     if (tableRef.current) {
  //       const tBodyEl = tableRef.current?.querySelector('.tea-table__body');
  //       if (tBodyEl) {
  //         if (!tableBodyRef.current) {
  //           tableBodyRef.current = tBodyEl;
  //         }
  //         tableBodyRef.current.addEventListener('scroll', handleScroll);
  //         // 防止加不到上面这个监听，还不懂为什么没有加到监听的原因
  //         const t2 = setTimeout(() => {
  //           clearInterval(t);
  //           clearTimeout(t2);
  //         }, 3000);
  //       }
  //     }
  //   }, 100);

  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //     if (tableBodyRef.current) {
  //       tableBodyRef.current?.removeEventListener('scroll', handleScroll);
  //     }
  //   };
  // }, []);

  // 初始化
  useEffect(() => {
    setLoading(propsLoading);

    if (!Array.isArray(tableRecords) || !tableRecords?.length) {
      setRecords([]);
      setPaginationMsg({ ...paginationMsg, pageIndex: 1 });
      return;
    }
    setRecords([...tableRecords]);
    if (tableRecords?.length) {
      const total = tableRecords?.length - (tableOptions?.isHasAllLine ? 1 : 0);
      setPaginationMsg({ ...paginationMsg, pageIndex: 1, total });
      recordsTotalRef.current = total;
    }
  }, [propsLoading]);

  // 默认排序不生效时
  useEffect(() => {
    const defaultSortBy = tableOptions?.sortableOptions?.defaultSort?.by;
    if (defaultSortBy && !sorts?.by) {
      setSorts({
        ...sorts,
        by: defaultSortBy,
      });
    }
  }, [tableOptions?.sortableOptions?.defaultSort?.by]);

  return (
    <Table
      ref={tableRef}
      className={classNames({
        'dbh-action-state-center-table': true,
        'dbg-common-table': true,
        // // 表格出现纵向滚动条
        // 'scroll-table-compatible': tableIsScroll,
        // // 最后一列有排序且表格出现纵向滚动条
        // 'scroll-table-compatible-last-col-header-th':
        //   tableIsScroll &&
        //   (tableOptions?.sortableOptions?.sortColumns || [])?.includes(
        //     lastColKeyName,
        //   ),
        [className]: className,
      })}
      bordered={bordered}
      // verticalTop
      records={loading ? [] : records}
      recordKey="uniqueId"
      columns={
        getColumns(
          className || 'dbg-common-table',
          tableIsScroll,
          tableOptions?.sortableOptions?.sortColumns,
          tableOptions?.columsOptions,
          unFoldList,
          setUnFoldList,
          triggerFoldExpand,
        ) as TableProps['columns']
      }
      addons={addons}
    />
  );
}

TableList.defaultProps = DEFAULT_PROPS;

export default memo(TableList);
